import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  onSave: () => Promise<void> | void;
  onDiscard?: () => void;
  message?: string;
}

interface UseUnsavedChangesReturn {
  showUnsavedModal: boolean;
  pendingNavigation: string | null;
  handleModalClose: () => void;
  handleDiscard: () => Promise<void>;
  handleSaveChanges: () => Promise<void>;
  isLoading: boolean;
}

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  onSave,
  onDiscard,
  message = 'Kaydedilmemiş değişiklikleriniz var. Sayfadan ayrılmak istediğinizden emin misiniz?',
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn => {
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowUnsavedModal(true);
      setPendingNavigation(blocker.location.pathname + blocker.location.search);
    }
  }, [blocker]);

  const handleModalClose = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
    blocker.reset?.();
  }, [blocker]);

  const handleDiscard = useCallback(async () => {
    try {
      setIsLoading(true);
      await Promise.resolve(onDiscard?.());
      setShowUnsavedModal(false);
      blocker.proceed?.();
    } catch (err) {
      console.error('Discard failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [blocker, onDiscard]);

  const handleSaveChanges = useCallback(async () => {
    try {
      setIsLoading(true);
      await Promise.resolve(onSave());
      setShowUnsavedModal(false);
      blocker.proceed?.();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [blocker, onSave]);

  return {
    showUnsavedModal,
    pendingNavigation,
    handleModalClose,
    handleDiscard,
    handleSaveChanges,
    isLoading,
  };
};
